import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Code, Cpu, Globe } from 'lucide-react';
import { Button } from './ui/Button';
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#0B1426]" data-id="element-130">
      {/* Tech Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-131">
        <div className="absolute inset-0 bg-[#0B1426]/40 group-hover:bg-[#0B1426]/0 transition-colors duration-500 z-10" data-id="element-132" />

        {/* Animated Orbs */}
        <div className="absolute -top-[20%] -right-[10%] w-[800px] h-[800px] rounded-full bg-[#d8a43e]/10 blur-[100px] animate-pulse" data-id="element-133" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[600px] h-[600px] rounded-full bg-blue-900/20 blur-[100px]" data-id="element-134" />

        {/* Floating Particles */}
        {[...Array(20)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-[#d8a43e]/30 rounded-full" initial={{
        x: Math.random() * 1000,
        y: Math.random() * 1000
      }} animate={{
        y: [0, -100],
        opacity: [0, 1, 0]
      }} transition={{
        duration: Math.random() * 5 + 5,
        repeat: Infinity,
        ease: 'linear',
        delay: Math.random() * 5
      }} style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`
      }} data-id="element-135" />)}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-136">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-137">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-138">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d8a43e]/5 border border-[#d8a43e]/20 text-[#d8a43e] text-xs font-mono mb-6 backdrop-blur-sm" data-id="element-139">
              <span className="relative flex h-2 w-2" data-id="element-140">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d8a43e] opacity-75" data-id="element-141"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d8a43e]" data-id="element-142"></span>
              </span>
              SYSTEM_STATUS: ONLINE // AVAILABLE_FOR_PROJECTS
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-[1.1] mb-6 tracking-tight" data-id="element-143">
              We Build{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d8a43e] to-[#f3d288] text-glow" data-id="element-144">
                Digital Experiences
              </span>{' '}
              That Drive Growth
            </h1>

            <p className="text-xl text-gray-400 mb-8 max-w-lg leading-relaxed border-l-2 border-[#d8a43e]/30 pl-6" data-id="element-145">
              Affordable web design & software development agency serving
              clients in London UK.
            </p>

            <div className="flex flex-wrap gap-4 mb-10" data-id="element-146">
              {['Website Design', 'Mobile Apps', 'UI/UX', 'Digital Marketing'].map((tag, i) => <motion.div key={tag} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + i * 0.1
            }} className="flex items-center gap-2 text-gray-300 text-sm bg-white/5 px-3 py-1.5 rounded-md border border-white/5" data-id="element-147">
                  <CheckCircle2 className="w-4 h-4 text-[#d8a43e]" data-id="element-148" />
                  {tag}
                </motion.div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-149">
              <a href="https://wa.me/447412843259?text=Hi%2C%20I%27m%20interested%20in%20starting%20a%20project%20with%20KM%20Consulting" target="_blank" rel="noopener noreferrer" data-id="element-150">
                <Button size="lg" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-152" />} data-id="element-151">
                  Start Your Project
                </Button>
              </a>
              <a href="#portfolio" data-id="element-153">
                <Button variant="secondary" size="lg" data-id="element-154">
                  View Our Work
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Visual Element */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative hidden lg:block" data-id="element-155">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#0C1525]/80 backdrop-blur-xl p-2 group" data-id="element-156">
              {/* Decorative Tech Borders */}
              <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-[#d8a43e]/30 rounded-tl-2xl" data-id="element-157" />
              <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-[#d8a43e]/30 rounded-br-2xl" data-id="element-158" />

              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015&auto=format&fit=crop" alt="Dashboard Preview" className="rounded-lg w-full h-auto shadow-lg opacity-80 group-hover:opacity-100 transition-opacity duration-500" data-id="element-159" />

              {/* Floating Cards */}
              <motion.div animate={{
              y: [0, -15, 0]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -bottom-8 -left-8 bg-[#0C1525] p-5 rounded-xl border border-[#d8a43e]/20 shadow-[0_0_30px_rgba(0,0,0,0.5)] backdrop-blur-md" data-id="element-160">
                <div className="flex items-center gap-4" data-id="element-161">
                  <div className="w-12 h-12 rounded-full bg-[#d8a43e]/10 flex items-center justify-center text-[#d8a43e] border border-[#d8a43e]/20" data-id="element-162">
                    <span className="font-bold font-mono" data-id="element-163">98%</span>
                  </div>
                  <div data-id="element-164">
                    <p className="text-xs text-[#d8a43e] font-mono mb-1" data-id="element-165">
                      SATISFACTION_RATE
                    </p>
                    <p className="text-sm font-bold text-white" data-id="element-166">
                      Based on 150+ reviews
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Icons */}
              <motion.div animate={{
              y: [0, 15, 0]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} className="absolute -top-6 -right-6 bg-[#0C1525] p-3 rounded-lg border border-white/10 shadow-xl" data-id="element-167">
                <Code className="text-[#d8a43e] w-6 h-6" data-id="element-168" />
              </motion.div>

              <motion.div animate={{
              x: [0, 10, 0]
            }} transition={{
              duration: 7,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.5
            }} className="absolute top-1/2 -right-10 bg-[#0C1525] p-3 rounded-lg border border-white/10 shadow-xl" data-id="element-169">
                <Cpu className="text-blue-400 w-6 h-6" data-id="element-170" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}